import React, { useState, useEffect } from "react";
import { getPeriodSummary, PeriodSummaryResponse } from "../../../../Rest_api/RestApi";
import { BarChart3, Activity, Zap } from "lucide-react";
import { SearchBox } from "../../../../components/Search";

interface SimulationSummaryTilesProps {
    availableDates: any[];
    selectedExchanges: string[];
    selectedMarkets: string[];
}

const SimulationSummaryTiles: React.FC<SimulationSummaryTilesProps> = ({
    availableDates,
    selectedExchanges,
    selectedMarkets,
}) => {
    // Stores the selected date objects from SearchBox
    // We keep it as an array for consistency with SearchBox, but it will only have 1 item
    const [selectedFilterDate, setSelectedFilterDate] = useState<any[]>([]);
    const [data, setData] = useState<PeriodSummaryResponse | null>(null);
    const [loading, setLoading] = useState(false);

    // 1. Sync selected dates with availableDates (remove stale selections)
    useEffect(() => {
        if (availableDates.length > 0) {
            const validSelectedDates = selectedFilterDate.filter((selected) =>
                availableDates.some((available) => available.value === selected.value)
            );

            if (validSelectedDates.length === 0) {
                // If no valid selection or selection cleared, default to the first available date
                setSelectedFilterDate([availableDates[0]]);
            } else if (validSelectedDates.length !== selectedFilterDate.length) {
                // If some were invalid, update state
                setSelectedFilterDate(validSelectedDates);
            }
        } else {
            // If no available dates, clear selection
            if (selectedFilterDate.length > 0) setSelectedFilterDate([]);
        }
    }, [availableDates]);

    // 2. Fetch data whenever selectedFilterDate (or filters) change
    useEffect(() => {
        const fetchData = async () => {
            if (selectedFilterDate.length === 0) {
                setData(null);
                return;
            }

            setLoading(true);
            try {
                // Use the FIRST selected date range (since isMany=false)
                const dateOption = selectedFilterDate[0];
                const dateRange = dateOption.value.split(",");
                const startDate = dateRange[0];
                const endDate = dateRange[1] || startDate;

                const result = await getPeriodSummary({
                    startDate,
                    endDate,
                    exchange: selectedExchanges.join(","),
                    product: selectedMarkets.join(","),
                });
                setData(result);
            } catch (error) {
                console.error("Error fetching summary tiles data", error);
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedFilterDate, selectedExchanges, selectedMarkets]);

    const formatValue = (val: number | undefined) => {
        if (val === undefined || val === null) return "-";
        return val.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    };

    const tiles = [
        {
            label: "Weighted Avg MCP",
            value: data ? `${formatValue(data.weighted_avg_mcp)} Rs/MWh` : "-",
            icon: <Zap className="w-4 h-4 text-white" />,
            bgColor: "bg-[#FFF6E9]", // Light Orange
            iconBg: "bg-[#F97316]", // Orange
        },
        {
            label: "Peak Demand",
            value: data ? `${formatValue(data.peak_demand)} MW` : "-",
            icon: <Activity className="w-4 h-4 text-white" />,
            bgColor: "bg-[#F3E8FF]", // Light Purple
            iconBg: "bg-[#A855F7]", // Purple
        },
        {
            label: "All India Demand",
            value: data ? `${formatValue(data.all_india_demand)} MU` : "-",
            icon: <BarChart3 className="w-4 h-4 text-white" />,
            bgColor: "bg-[#ECFDF5]", // Light Green
            iconBg: "bg-[#22C55E]", // Green
        },
        {
            label: "All India Generation",
            value: data ? `${formatValue(data.all_india_generation)} MU` : "-",
            icon: <BarChart3 className="w-4 h-4 text-white" />,
            bgColor: "bg-[#EFF6FF]", // Light Blue
            iconBg: "bg-[#3B82F6]", // Blue
        },
    ];

    return (
        <div className="flex flex-row gap-2 w-full items-stretch">
            {tiles.map((tile, index) => (
                <div
                    key={index}
                    className={`${tile.bgColor} flex-1 rounded-xl px-2 py-1.5 flex items-center justify-between border border-transparent hover:border-gray-200 transition-all shadow-sm`}
                >
                    <div className="flex items-center gap-2 w-full">
                        <div
                            className={`${tile.iconBg} p-1.5 rounded-lg shadow-sm flex items-center justify-center`}
                        >
                            {tile.icon}
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-normal leading-tight truncate">
                                {tile.label}
                            </span>
                            <div className="flex flex-row items-baseline gap-2 mt-0.5">
                                {loading ? (
                                    <div className="h-3 w-16 bg-gray-200/50 animate-pulse rounded"></div>
                                ) : (
                                    <span className="text-sm font-extrabold text-gray-800 leading-tight">
                                        {tile.value}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            <div className="w-[220px] bg-white rounded-lg border border-gray-100 shadow-sm p-1 flex flex-col justify-center">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-1">
                    Select Date
                </span>
                <div className="[&_div]:!text-xs [&>div>div]:!min-h-[28px] [&>div>div]:!py-0 [&>div>div]:!border-0 [&>div>div]:!bg-gray-50 text-gray-700">
                    <SearchBox
                        options={availableDates}
                        isMany={false}
                        placeholder="Select Date"
                        selectedOptions={selectedFilterDate}
                        onChange={(val: any) => {
                            if (val) {
                                setSelectedFilterDate(Array.isArray(val) ? val : [val]);
                            } else {
                                setSelectedFilterDate([]);
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default SimulationSummaryTiles;
